/**
 * ByProductStep — Step 3 of the waste tracking wizard.
 * Dynamic entry lists for Marketable and Production by-products.
 * Extracted from MobileWasteTracking.tsx for maintainability.
 */
import React from 'react';
import toast from 'react-hot-toast';
import { ArrowLeft, ArrowRight, X, Plus } from 'lucide-react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { ByproductMapping } from '../../../services/byproductMappingService';
import type { ByproductEntry } from '../../../utils/mobileSessionStorage';
import type { MaterialInfo } from '../wasteTrackingTypes';

interface ByProductStepProps {
  materials: MaterialInfo[];
  byproductMappings: ByproductMapping[];
  selectedMaterialByProductMarketable: string;
  setSelectedMaterialByProductMarketable: (v: string) => void;
  setSelectedMaterialByProductProduction: (v: string) => void;
  byproductMarketableEntries: ByproductEntry[];
  setByproductMarketableEntries: React.Dispatch<React.SetStateAction<ByproductEntry[]>>;
  byproductProductionEntries: ByproductEntry[];
  setByproductProductionEntries: React.Dispatch<React.SetStateAction<ByproductEntry[]>>;
  byProductMarketable: number;
  byProductProduction: number;
  goBack: () => void;
  goNext: () => void;
}

const ByProductStep: React.FC<ByProductStepProps> = ({
  materials,
  byproductMappings,
  selectedMaterialByProductMarketable,
  setSelectedMaterialByProductMarketable,
  setSelectedMaterialByProductProduction,
  byproductMarketableEntries,
  setByproductMarketableEntries,
  byproductProductionEntries,
  setByproductProductionEntries,
  byProductMarketable,
  byProductProduction,
  goBack,
  goNext,
}) => {
  const { t } = useLanguage();

  return (
    <>
      <div className="flex-1 flex flex-col justify-center overflow-y-auto">
        <div className="w-full max-w-md md:max-w-lg lg:max-w-xl mx-auto">
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white text-center mb-1">
            {t('mobile.byProduct') || 'By-Product'}
          </h3>
          <p className="text-sm md:text-base text-gray-500 text-center mb-4">
            {t('mobile.byProductDesc') || 'Sellable or reusable by-product'}
          </p>

          {/* Raw material dropdown (multi-material sessions) - shared for both types */}
          {materials.length > 1 && (
            <div className="mb-3">
              <label className="text-xs text-gray-500 mb-1 block">{t('mobile.selectMaterial') || 'Select Material'}</label>
              <select
                value={selectedMaterialByProductMarketable}
                onChange={(e) => { setSelectedMaterialByProductMarketable(e.target.value); setSelectedMaterialByProductProduction(e.target.value); }}
                className="w-full px-3 py-2 rounded-xl border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              >
                {materials.map(m => (
                  <option key={m.item_number} value={m.item_number}>
                    {m.item_name || m.item_number}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Marketable By-Product Section */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-yellow-700 dark:text-yellow-300">
                  {t('mobile.byProductMarketable') || 'Marketable'}
                </span>
                <span className="text-xs text-gray-500">
                  ({t('mobile.byProductMarketableDesc') || 'can be sold'})
                </span>
              </div>
              {byProductMarketable > 0 && (
                <span className="text-xs font-bold text-yellow-600 dark:text-yellow-400">{byProductMarketable.toFixed(2)} lbs</span>
              )}
            </div>
            {/* Dynamic entry rows — stacked layout: dropdown full-width on top, weight + remove below */}
            <div className="space-y-3 mb-2">
              {byproductMarketableEntries.map((entry, idx) => {
                const selectedItems = [...byproductMarketableEntries.filter((_, i) => i !== idx), ...byproductProductionEntries].map(e => e.byproductItemNumber).filter(Boolean);
                const availableMappings = byproductMappings.filter(m => !selectedItems.includes(m.byproduct_item_number));
                return (
                  <div key={idx} className="bg-yellow-50 dark:bg-yellow-900/10 rounded-xl p-3 border border-yellow-200 dark:border-yellow-700">
                    {/* Row 1: Remove button + Dropdown */}
                    <div className="flex items-center gap-2 mb-2">
                      <button
                        onClick={() => setByproductMarketableEntries(prev => prev.filter((_, i) => i !== idx))}
                        className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl flex-shrink-0"
                      >
                        <X className="w-5 h-5" />
                      </button>
                      <select
                        value={entry.byproductItemNumber}
                        onChange={(e) => {
                          const updated = [...byproductMarketableEntries];
                          updated[idx] = { ...updated[idx], byproductItemNumber: e.target.value };
                          setByproductMarketableEntries(updated);
                        }}
                        className="flex-1 min-w-0 px-3 py-2.5 rounded-xl border-2 border-yellow-300 dark:border-yellow-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                      >
                        {byproductMappings.length === 0 ? (
                          <option value="">{t('mobile.noLinkedByproducts') || 'No linked by-products'}</option>
                        ) : (
                          <>
                            <option value="" disabled>{t('mobile.selectByproduct') || 'Select by-product'}</option>
                            {availableMappings.map(m => (
                              <option key={`mk-${m.id}`} value={m.byproduct_item_number}>
                                {m.byproduct_item_number} - {m.byproduct_name || ''}{m.byproduct_chinese_name ? ` / ${m.byproduct_chinese_name}` : ''}
                              </option>
                            ))}
                            {entry.byproductItemNumber && !availableMappings.some(m => m.byproduct_item_number === entry.byproductItemNumber) && (
                              <option value={entry.byproductItemNumber}>
                                {entry.byproductItemNumber} - {byproductMappings.find(m => m.byproduct_item_number === entry.byproductItemNumber)?.byproduct_name || ''}
                              </option>
                            )}
                          </>
                        )}
                      </select>
                    </div>
                    {/* Row 2: Weight input — full width */}
                    <div className="relative">
                      <input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        value={entry.weight}
                        onChange={(e) => {
                          const updated = [...byproductMarketableEntries];
                          updated[idx] = { ...updated[idx], weight: e.target.value };
                          setByproductMarketableEntries(updated);
                        }}
                        placeholder="0.00"
                        className="w-full text-center text-3xl md:text-4xl font-bold py-3 px-4 rounded-xl border-2 border-yellow-300 dark:border-yellow-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:border-yellow-500"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-medium">lbs</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <button
              onClick={() => {
                if (byproductMappings.length === 0) { toast(t('mobile.noByproductMappings')); return; }
                setByproductMarketableEntries(prev => [...prev, { byproductItemNumber: '', weight: '' }]);
              }}
              className={`w-full py-3 border-2 border-dashed border-yellow-300 dark:border-yellow-600 text-yellow-700 dark:text-yellow-300 rounded-xl text-base font-medium flex items-center justify-center gap-1.5 ${byproductMappings.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-yellow-50 dark:hover:bg-yellow-900/10'}`}
            >
              <Plus className="w-5 h-5" />
              {t('mobile.addByproductEntry') || 'Add by-product'}
            </button>
          </div>

          {/* Used for Production By-Product Section */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-green-700 dark:text-green-300">
                  {t('mobile.byProductProduction') || 'Used for Production'}
                </span>
                <span className="text-xs text-gray-500">
                  ({t('mobile.byProductProductionDesc') || 'reused in production'})
                </span>
              </div>
              {byProductProduction > 0 && (
                <span className="text-xs font-bold text-green-600 dark:text-green-400">{byProductProduction.toFixed(2)} lbs</span>
              )}
            </div>
            {/* Dynamic entry rows — stacked layout */}
            <div className="space-y-3 mb-2">
              {byproductProductionEntries.map((entry, idx) => {
                const selectedItems = [...byproductProductionEntries.filter((_, i) => i !== idx), ...byproductMarketableEntries].map(e => e.byproductItemNumber).filter(Boolean);
                const availableMappings = byproductMappings.filter(m => !selectedItems.includes(m.byproduct_item_number));
                return (
                  <div key={idx} className="bg-green-50 dark:bg-green-900/10 rounded-xl p-3 border border-green-200 dark:border-green-700">
                    {/* Row 1: Remove button + Dropdown */}
                    <div className="flex items-center gap-2 mb-2">
                      <button
                        onClick={() => setByproductProductionEntries(prev => prev.filter((_, i) => i !== idx))}
                        className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl flex-shrink-0"
                      >
                        <X className="w-5 h-5" />
                      </button>
                      <select
                        value={entry.byproductItemNumber}
                        onChange={(e) => {
                          const updated = [...byproductProductionEntries];
                          updated[idx] = { ...updated[idx], byproductItemNumber: e.target.value };
                          setByproductProductionEntries(updated);
                        }}
                        className="flex-1 min-w-0 px-3 py-2.5 rounded-xl border-2 border-green-300 dark:border-green-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                      >
                        {byproductMappings.length === 0 ? (
                          <option value="">{t('mobile.noLinkedByproducts') || 'No linked by-products'}</option>
                        ) : (
                          <>
                            <option value="" disabled>{t('mobile.selectByproduct') || 'Select by-product'}</option>
                            {availableMappings.map(m => (
                              <option key={`pr-${m.id}`} value={m.byproduct_item_number}>
                                {m.byproduct_item_number} - {m.byproduct_name || ''}{m.byproduct_chinese_name ? ` / ${m.byproduct_chinese_name}` : ''}
                              </option>
                            ))}
                            {entry.byproductItemNumber && !availableMappings.some(m => m.byproduct_item_number === entry.byproductItemNumber) && (
                              <option value={entry.byproductItemNumber}>
                                {entry.byproductItemNumber} - {byproductMappings.find(m => m.byproduct_item_number === entry.byproductItemNumber)?.byproduct_name || ''}
                              </option>
                            )}
                          </>
                        )}
                      </select>
                    </div>
                    {/* Row 2: Weight input — full width */}
                    <div className="relative">
                      <input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        value={entry.weight}
                        onChange={(e) => {
                          const updated = [...byproductProductionEntries];
                          updated[idx] = { ...updated[idx], weight: e.target.value };
                          setByproductProductionEntries(updated);
                        }}
                        placeholder="0.00"
                        className="w-full text-center text-3xl md:text-4xl font-bold py-3 px-4 rounded-xl border-2 border-green-300 dark:border-green-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:border-green-500"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-medium">lbs</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <button
              onClick={() => {
                if (byproductMappings.length === 0) { toast(t('mobile.noByproductMappings')); return; }
                setByproductProductionEntries(prev => [...prev, { byproductItemNumber: '', weight: '' }]);
              }}
              className={`w-full py-3 border-2 border-dashed border-green-300 dark:border-green-600 text-green-700 dark:text-green-300 rounded-xl text-base font-medium flex items-center justify-center gap-1.5 ${byproductMappings.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-50 dark:hover:bg-green-900/10'}`}
            >
              <Plus className="w-5 h-5" />
              {t('mobile.addByproductEntry') || 'Add by-product'}
            </button>
          </div>
        </div>
      </div>
      <div className="flex gap-3 mt-4">
        <button onClick={goBack} className="flex-1 py-4 md:py-5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold text-lg md:text-xl flex items-center justify-center gap-2">
          <ArrowLeft className="w-5 h-5" />
          {t('common.back') || 'Back'}
        </button>
        <button onClick={goNext} className="flex-1 py-4 md:py-5 bg-amber-500 text-white rounded-xl font-semibold text-lg md:text-xl flex items-center justify-center gap-2">
          {t('common.next') || 'Next'}
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </>
  );
};

export default ByProductStep;
